// Google Analytics 4 Event Tracking Utility
// Measurement ID: G-VLE2X8G0K9

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

type EventParams = {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
};

/**
 * Track a custom event in Google Analytics 4
 */
export const trackEvent = (
  eventName: string,
  params?: EventParams
): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// ============================================
// CTA & Button Click Events
// ============================================

export const trackCTAClick = (
  ctaName: string,
  location: string,
  additionalParams?: Record<string, any>
): void => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_location: location,
    ...additionalParams,
  });
};

export const trackButtonClick = (
  buttonName: string,
  location: string
): void => {
  trackEvent('button_click', {
    event_category: 'interaction',
    event_label: buttonName,
    button_location: location,
  });
};

// ============================================
// Form Events
// ============================================

export const trackFormStart = (formName: string): void => {
  trackEvent('form_start', {
    event_category: 'form',
    event_label: formName,
  });
};

export const trackFormSubmit = (
  formName: string,
  success: boolean = true
): void => {
  trackEvent('form_submit', {
    event_category: 'form',
    event_label: formName,
    form_success: success,
  });
};

export const trackFormError = (
  formName: string,
  errorType: string
): void => {
  trackEvent('form_error', {
    event_category: 'form',
    event_label: formName,
    error_type: errorType,
  });
};

// ============================================
// Navigation Events
// ============================================

export const trackNavigation = (
  destination: string,
  source: string = 'header'
): void => {
  trackEvent('navigation', {
    event_category: 'navigation',
    event_label: destination,
    navigation_source: source,
  });
};

// ============================================
// Content Engagement Events
// ============================================

export const trackVideoPlay = (videoName: string): void => {
  trackEvent('video_play', {
    event_category: 'video',
    event_label: videoName,
  });
};

export const trackDownload = (fileName: string, fileType: string): void => {
  trackEvent('file_download', {
    event_category: 'download',
    event_label: fileName,
    file_type: fileType,
  });
};

export const trackExternalLink = (url: string, linkText: string): void => {
  trackEvent('external_link_click', {
    event_category: 'outbound',
    event_label: linkText,
    destination_url: url,
  });
};

// ============================================
// Tool Usage Events
// ============================================

export const trackToolStart = (toolName: string): void => {
  trackEvent('tool_start', {
    event_category: 'tools',
    event_label: toolName,
  });
};

export const trackToolComplete = (
  toolName: string,
  resultScore?: number
): void => {
  trackEvent('tool_complete', {
    event_category: 'tools',
    event_label: toolName,
    value: resultScore,
  });
};

// ============================================
// Page-specific Events
// ============================================

export const trackPageSection = (
  sectionName: string,
  action: 'view' | 'scroll' | 'interact'
): void => {
  trackEvent('page_section', {
    event_category: 'content',
    event_label: sectionName,
    section_action: action,
  });
};
